import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../AuthContext';
import { useLanguage } from '../../App';
import { PetSprite } from './PetSprites';
import { motion, AnimatePresence } from 'framer-motion';
import { Heart, Utensils, Sparkles, Star, ChevronRight, Coins } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PET_INFO = {
  harimau: { name_bm: 'Harimau Malaya', name_en: 'Malayan Tiger', color: '#F97316', desc_bm: 'Perkasa dan berani', desc_en: 'Mighty and brave' },
  enggang: { name_bm: 'Burung Enggang', name_en: 'Hornbill', color: '#FBBF24', desc_bm: 'Bijak dan setia', desc_en: 'Wise and loyal' },
  penyu: { name_bm: 'Penyu Agar-Agar', name_en: 'Sea Turtle', color: '#34D399', desc_bm: 'Sabar dan tabah', desc_en: 'Patient and resilient' },
};

const STAGE_NAMES = {
  1: { bm: 'Bayi', en: 'Baby' },
  2: { bm: 'Kanak-kanak', en: 'Child' },
  3: { bm: 'Remaja', en: 'Teen' },
  4: { bm: 'Dewasa', en: 'Adult' },
};

export default function PetWidget({ compact = false }) {
  const { token } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [pet, setPet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [feeding, setFeeding] = useState(false);
  const [feedResult, setFeedResult] = useState(null);
  const isEN = language === 'en';

  useEffect(() => {
    if (!token) return;
    fetch(`${BACKEND_URL}/api/pet/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(data => { setPet(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [token]);

  const feedPet = async () => {
    if (feeding) return;
    setFeeding(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/pet/feed`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      const data = await res.json();
      if (data.success) {
        setFeedResult({ type: 'success', msg: isEN ? 'Yum!' : 'Sedap!' });
        // Keep eating animation for 1.5s before updating
        setTimeout(() => {
          setPet(prev => prev ? { ...prev, hunger: data.new_hunger } : prev);
          setFeeding(false);
          setTimeout(() => setFeedResult(null), 1500);
        }, 1500);
        return;
      } else {
        setFeedResult({ type: 'error', msg: data.detail || (isEN ? 'Not enough coins' : 'Syiling tak cukup') });
        setTimeout(() => setFeedResult(null), 2500);
      }
    } catch {
      setFeedResult({ type: 'error', msg: isEN ? 'Error' : 'Ralat' });
      setTimeout(() => setFeedResult(null), 2000);
    }
    setFeeding(false);
  };

  if (loading) return null;
  if (!pet || !pet.has_pet) {
    return (
      <motion.div
        data-testid="pet-widget-empty"
        className="pet-widget-empty"
        initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
        onClick={() => navigate('/pet-select')}
        style={{
          background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
          border: '1px dashed #334155',
          borderRadius: 20, padding: '20px 16px', cursor: 'pointer',
          display: 'flex', alignItems: 'center', gap: 14, marginBottom: 12,
          transition: 'border-color 0.2s',
        }}
        whileHover={{ borderColor: '#38bdf8' }}
      >
        <div style={{ width: 52, height: 52, borderRadius: 14, background: '#1e293b', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24 }}>
          <Sparkles size={24} color="#fbbf24" />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#f8fafc' }}>{isEN ? 'Adopt a Pet!' : 'Adopt Haiwan Peliharaan!'}</div>
          <div style={{ fontSize: 12, color: '#94a3b8' }}>{isEN ? 'Choose your companion' : 'Pilih teman belajar anda'}</div>
        </div>
        <ChevronRight size={18} color="#475569" />
      </motion.div>
    );
  }

  const info = PET_INFO[pet.pet_type] || {};
  const stageName = STAGE_NAMES[pet.stage] || {};
  const hungerPct = Math.max(0, Math.min(100, pet.hunger));
  const happyPct = Math.max(0, Math.min(100, pet.happiness));

  // Rich mood system based on multiple factors
  let mood = 'happy';
  if (feeding) mood = 'eating';
  else if (hungerPct < 15) mood = 'hungry';
  else if (happyPct < 20) mood = 'sad';
  else if (happyPct > 80 && hungerPct > 60) mood = 'excited';

  const xpPct = pet.next_stage ? Math.min(100, ((pet.pet_xp - (pet.stage_info?.xp_required || 0)) / (pet.next_stage.xp_required - (pet.stage_info?.xp_required || 0))) * 100) : 100;

  return (
    <motion.div
      data-testid="pet-widget"
      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
      style={{
        background: '#0f172a', border: '1px solid #1e293b', borderRadius: 20,
        padding: compact ? '14px' : '18px', marginBottom: 12, position: 'relative', overflow: 'hidden',
        cursor: 'pointer',
      }}
      onClick={(e) => { if (e.target.closest('[data-testid="feed-pet-btn"]')) return; navigate('/pet'); }}
    >
      {/* Glow effect */}
      <div style={{ position: 'absolute', top: -20, right: -20, width: 80, height: 80, borderRadius: '50%', background: info.color, opacity: 0.06, filter: 'blur(20px)' }} />

      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
        {/* Pet sprite */}
        <div data-testid="pet-sprite" style={{ flexShrink: 0 }}>
          <PetSprite petType={pet.pet_type} stage={pet.stage} size={compact ? 56 : 72} mood={mood} accessories={pet.accessories || []} />
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Name & Stage */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
            <span data-testid="pet-name" style={{ fontSize: 15, fontWeight: 700, color: '#f8fafc', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{pet.pet_name}</span>
            <span style={{ fontSize: 11, fontWeight: 600, color: info.color, background: `${info.color}20`, padding: '2px 8px', borderRadius: 100 }}>
              {isEN ? stageName.en : stageName.bm}
            </span>
          </div>

          {/* XP progress to next stage */}
          <div style={{ marginBottom: 6 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#64748b', marginBottom: 3 }}>
              <span>{isEN ? 'Evolution' : 'Evolusi'}</span>
              <span>{pet.pet_xp} XP {pet.next_stage ? `/ ${pet.next_stage.xp_required}` : '(MAX)'}</span>
            </div>
            <div style={{ height: 5, background: '#1e293b', borderRadius: 100, overflow: 'hidden' }}>
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${xpPct}%` }}
                transition={{ duration: 1, ease: 'easeOut' }}
                style={{ height: '100%', background: `linear-gradient(90deg, ${info.color}, ${info.color}cc)`, borderRadius: 100 }}
              />
            </div>
          </div>

          {/* Hunger & Happiness bars */}
          <div style={{ display: 'flex', gap: 10 }}>
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 10, color: '#94a3b8', marginBottom: 2 }}>
                <Utensils size={10} /> {isEN ? 'Hunger' : 'Lapar'}
              </div>
              <div style={{ height: 4, background: '#1e293b', borderRadius: 100, overflow: 'hidden' }}>
                <div style={{ width: `${hungerPct}%`, height: '100%', background: hungerPct > 30 ? '#34d399' : hungerPct > 10 ? '#fbbf24' : '#f43f5e', borderRadius: 100, transition: 'width 0.5s' }} />
              </div>
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 10, color: '#94a3b8', marginBottom: 2 }}>
                <Heart size={10} /> {isEN ? 'Happy' : 'Gembira'}
              </div>
              <div style={{ height: 4, background: '#1e293b', borderRadius: 100, overflow: 'hidden' }}>
                <div style={{ width: `${happyPct}%`, height: '100%', background: happyPct > 30 ? '#38bdf8' : happyPct > 10 ? '#fbbf24' : '#f43f5e', borderRadius: 100, transition: 'width 0.5s' }} />
              </div>
            </div>
          </div>
        </div>

        {/* Feed button */}
        {!compact && (
          <motion.button
            data-testid="feed-pet-btn"
            onClick={feedPet}
            disabled={feeding || hungerPct >= 95}
            whileTap={hungerPct < 95 ? { scale: 0.9 } : {}}
            style={{
              background: hungerPct >= 95 ? '#0f172a' : hungerPct < 50 ? '#F97316' : '#1e293b',
              border: hungerPct >= 95 ? '1px solid #1e293b' : hungerPct < 50 ? 'none' : '1px solid #334155',
              borderRadius: 12, padding: '10px 12px',
              cursor: hungerPct >= 95 ? 'not-allowed' : 'pointer',
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3,
              color: hungerPct >= 95 ? '#334155' : hungerPct < 50 ? 'white' : '#94a3b8',
              transition: 'all 0.2s', opacity: hungerPct >= 95 ? 0.5 : 1,
            }}
            title={hungerPct >= 95 ? (isEN ? 'Pet is full!' : 'Dah kenyang!') : ''}
          >
            <Utensils size={16} />
            <span style={{ fontSize: 9, fontWeight: 600 }}>
              {hungerPct >= 95 ? (isEN ? 'Full' : 'Kenyang') : <><Coins size={8} style={{ display: 'inline', verticalAlign: 'middle' }} /> 10</>}
            </span>
          </motion.button>
        )}
      </div>

      {/* Feed result toast */}
      <AnimatePresence>
        {feedResult && (
          <motion.div
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
            style={{
              position: 'absolute', bottom: 4, left: '50%', transform: 'translateX(-50%)',
              background: feedResult.type === 'success' ? '#34d399' : '#f43f5e',
              color: 'white', fontSize: 12, fontWeight: 700, padding: '4px 14px', borderRadius: 100,
            }}
          >
            {feedResult.msg}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Low stat warning */}
      {!compact && (hungerPct < 25 || happyPct < 25) && (
        <motion.div
          data-testid="pet-low-stat-warning"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }}
          style={{ marginTop: 8, fontSize: 12, color: '#fbbf24', display: 'flex', alignItems: 'center', gap: 4 }}
        >
          <Sparkles size={12} />
          {hungerPct < 25
            ? (isEN ? `${pet.pet_name} is hungry!` : `${pet.pet_name} lapar!`)
            : (isEN ? `${pet.pet_name} wants to play!` : `${pet.pet_name} nak bermain!`)}
        </motion.div>
      )}
    </motion.div>
  );
}
